package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/scoped_slots")
@Composable
fun ScopedSlotsComposePage() {
    BlogLayout(
        PageProperties(
            "Scoped Slots",
            "Scoped slots refer to content areas in a layout that provide additional, layout-specific capabilities to the composables placed inside them."
        ),
        BlogProperties("Jetpack Compose", "Scoped Slots / Slot APIs")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Slot APIs",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Slot API is a design pattern in Jetpack Compose that allows developers to pass Composable functions as parameters to other Composable functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Slot APIs (trailing @Composable lambda parameters) to make composables flexible without coupling them to specific child content (Slot APIs (passing @Composable () - Unit) are a powertul pattern for building flexible and decoupled Ul layouts. They allow the top-level screen to define content for a leaf node without intermediate layers ever knowing the data requirements of that leaf).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun Card(
                        modifier: Modifier = Modifier,
                        content: @Composable ColumnScope.() -> Unit
                    ) {
                        Surface(modifier = modifier, shape = MaterialTheme.shapes.medium) {
                            Column(modifier = Modifier.padding(16.dp), content = content)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun FlexibleCard(
                        header: @Composable () -> Unit,
                        body: @Composable () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Card(modifier = modifier) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                // 1. Render the caller's custom header Composable
                                header() 
                                
                                HorizontalDivider(
                                    modifier = Modifier.padding(vertical = 8.dp)
                                )
                                
                                // 2. Render the caller's custom body Composable
                                body() 
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Scoped Slots",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, scoped slots refer to content areas in a layout that provide additional, layout-specific capabilities to the composables placed inside them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable slotting refers to the ability to pass composable functions as parameters to other composable functions, similar to how lambda functions are passed in traditional Kotlin programming. This enables the creation of components that can accept various UI elements dynamically, making them more flexible and reusable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose allows us to define slots using lambda parameters of type @Composable () -> Unit. We can define multiple slots for different UI sections, making components even more flexible.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike a regular slot (which simply accepts content), a scoped slot augments that content with extra behaviour such as:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "special modifiers",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "alignment rules",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "layout-specific constraints",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A scoped slot is a space within a composable where children gain special layout powers that they wouldn’t have elsewhere.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Scope (RowScope, ColumnScope, BoxScope, LazyListScope, LazyGridItemScope, DrawScope, AnimatedVisibilityScope)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose provides a declarative, GPU-accelerated drawing API via Canvas and DrawScope, replacing imperative onDraw(Canvas) from the View system.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Canvas is a Composable, lambda runs inside a DrawScope.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "DrawScope provides: Size information, Density-aware drawing, High-level primitives (drawRect, drawPath, drawArc, etc.)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "That RowScope is an interface that exposes special layout behaviour to children inside the Row.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://github.com/odaridavid/Smoovie-KMP.git
            KotlinCode(
                code = """
                    LazyColumn(
                        state = listState,
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(bottom = 16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                                   
                    }
                    
                    internal fun LazyListScope.movieItems(
                        movies: List<MovieUiModel>,
                        animatedIds: MutableSet<Int>,
                        isLoadingMore: Boolean,
                        onMovieClick: (MovieUiModel) -> Unit,
                    ) {
                        itemsIndexed(movies, key = { _, movie -> movie.id }) { index, movie ->
                            Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/thementalgoose/kmp-flashback.git
            KotlinCode(
                code = """
                    @Composable
                    fun AppScaffold(
                        modifier: Modifier = Modifier,
                        contentInsets: WindowInsets = WindowInsets.safeDrawingHorizontalOnly,
                        bottomBar: @Composable () -> Unit = { },
                        content: @Composable BoxScope.(PaddingValues) -> Unit,
                    ) {
                        Scaffold(
                            contentWindowInsets = contentInsets,
                            modifier = modifier,
                            bottomBar = bottomBar,
                            snackBarHost = {
                               
                            },
                            containerColor = AppTheme.colors.surface,
                            content = {
                                val paddingValues = WindowInsets.safeDrawing
                                    .exclude(contentInsets)
                                    .asPaddingValues()
                                Box(Modifier
                                    .fillMaxSize()
                                    .background(AppTheme.colors.surface)
                                    .windowInsetsPadding(contentInsets)
                                    .consumeWindowInsets(contentInsets)
                                ) {
                                    content(paddingValues)
                                }
                            }
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/thementalgoose/kmp-flashback.git
            KotlinCode(
                code = """
                    @Composable
                    fun Fade(
                        visible: Boolean,
                        modifier: Modifier = Modifier,
                        content: @Composable AnimatedVisibilityScope.() -> Unit,
                    ) {
                        AnimatedVisibility(
                            modifier = modifier,
                            visible = visible,
                            enter = fadeIn(),
                            exit = fadeOut(),
                            content = {
                                content(this)
                            }
                        )
                    }
                    """.trimIndent()
            )

            //https://medium.com/@nicholausmichael51/advanced-slot-patterns-in-jetpack-compose-scoped-slots-48447dd53722
            KotlinCode(
                code = """
                    // Our scope - gives access to the expanded state
                    interface ExpandableSectionScope {
                        val isExpanded: Boolean

                        @Composable
                        fun Content(content: @Composable () -> Unit)
                    }

                    // Implementation
                    private class ExpandableSectionScopeImpl(
                        override val isExpanded: Boolean
                    ) : ExpandableSectionScope {

                        @Composable
                        override fun Content(content: @Composable () -> Unit) {
                            AnimatedVisibility(visible = isExpanded) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(16.dp)
                                ) {
                                    content()
                                }
                            }
                        }
                    }

                    // Our expandable section
                    @Composable
                    fun ExpandableSection(
                        title: String,
                        modifier: Modifier = Modifier,
                        initiallyExpanded: Boolean = false,
                        content: @Composable ExpandableSectionScope.() -> Unit
                    ) {
                        var expanded by remember { mutableStateOf(initiallyExpanded) }

                        Column(modifier = modifier) {
                            // Header
                            Surface(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { expanded = !expanded },
                                color = MaterialTheme.colorScheme.surfaceVariant
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(16.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = title,
                                        style = MaterialTheme.typography.titleMedium
                                    )
                                    Icon(
                                        imageVector = if (expanded) {
                                            Icons.Filled.KeyboardArrowDown
                                        } else {
                                            Icons.Filled.KeyboardArrowUp
                                        },
                                        contentDescription = if (expanded) "Collapse" else "Expand"
                                    )
                                }
                            }

                            // Content with scope
                            ExpandableSectionScopeImpl(expanded).content()
                        }
                    }

                    @Preview(showBackground = true, name = "Collapsed")
                    @Composable
                    fun ExpandableSectionPreview() {
                        // Usage
                        SlotArticleTheme {
                            ExpandableSection(title = "Details") {
                                Content {
                                    Text("Name: John Doe")
                                    Text("Email: john@example.com")

                                    if (isExpanded) {
                                        // Only show this when expanded
                                        Text("Loaded additional details!")
                                    }
                                }
                            }
                        }
                    }

                    @Preview(showBackground = true, name = "Expanded")
                    @Composable
                    fun ExpandableSectionExpandedPreview() {
                        SlotArticleTheme {
                            ExpandableSection(
                                title = "Details",
                                initiallyExpanded = true
                            ) {
                                Content {
                                    Text("Name: Kamlesh Lakhani")
                                    Text("Email: kamal.lakhani56@gmail.com")

                                    if (isExpanded) {
                                        Text("Loaded additional details!")
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun interface SubmitAction : ToolbarAction

                    // Concrete implementation to be used by the developer
                    class PrimarySubmitAction(
                        private val label: String = "Submit",
                        private val onClick: () -> Unit
                    ) : SubmitAction {
                        @Composable
                        override fun invoke() {
                            Button(onClick = onClick) {
                                Text(text = label)
                            }
                        }
                    }
                    
                    // Tip: Use Factory Functions to improve API discoverability
                    fun SubmitAction(onClick: () -> Unit): SubmitAction = PrimarySubmitAction(onClick = onClick)
                    
                    @Composable
                    fun FormScreen(onSave: () -> Unit) {
                        Scaffold(
                            topBar = {
                                MyFormToolbar(
                                    title = "Edit Profile",
                                    // The compiler forces us to provide a valid SubmitAction
                                    submitAction = SubmitAction(onClick = onSave),
                                    optionalActions = listOf(
                                        ToolbarAction { TextButton(onClick = {}) { Text("Cancel") } }
                                    )
                                )
                            }
                        ) { /* content */ }
                    }

                    @Composable
                    fun MyFormToolbar(
                        title: String,
                        submitAction: SubmitAction,
                        optionalActions: List<ToolbarAction> = emptyList()
                    ) {
                        CenterAlignedTopAppBar(
                            title = { Text(title) },
                            actions = {
                                optionalActions.forEach { it() }
                                // Styling and presence are guaranteed by the SubmitAction type
                                submitAction()
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    LazyColumn(
                        contentPadding = PaddingValues(vertical = LocalDimens.current.padding.contentVertical),
                        verticalArrangement = Arrangement.spacedBy(LocalDimens.current.padding.itemVertical),
                        modifier = Modifier.fillMaxSize().padding(paddingValues),
                    ) {
                        items(onAction)
                    }
                    
                    private fun LazyListScope.items(onAction: (Action) -> Unit) {
                        item(key = "input") {
                           
                        }

                        item(key = "details_divider", contentType = "divider") {
                           
                        }

                        itemsIndexed(items = state.items, key = { _, item -> item.id }) { index, item ->
                            
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun DrawScope.drawBorder(
                        borderColor: Color,
                        cornerRadiusValue: Float
                    ) {
                        val dashPattern = floatArrayOf(15f, 5f)
                        val phase = 0f
                        val strokeWidth = 1.dp
                        val strokeWidthPx = strokeWidth.toPx()

                        drawRoundRect(
                            color = borderColor,
                            topLeft = Offset(
                                x = strokeWidthPx / 2,
                                y = strokeWidthPx / 2
                            ),
                            size = Size(
                                width = size.width - strokeWidthPx,
                                height = size.height - strokeWidthPx
                            ),
                            cornerRadius = CornerRadius(
                                x = cornerRadiusValue,
                                y = cornerRadiusValue
                            ),
                            style = Stroke(
                                width = strokeWidth.toPx(),
                                pathEffect = PathEffect.dashPathEffect(intervals = dashPattern, phase),
                            )
                        )
                    }
                    
                    @Composable
                    fun UploadProductImage(
                        modifier: Modifier = Modifier,
                        onUploadImageClick: (imageFile: ImageFile) -> Unit = {},
                        isUploadingImageEnabled: Boolean = false
                    ) {
                        Box(
                            modifier = modifier
                                .size(size = 88.dp)
                                .background(
                                    color = Theme.colorScheme.background.surfaceLow,
                                    shape = SquircleShape(12.dp)
                                )
                                .clip(SquircleShape(12.dp))
                                .clickable(onClick = { }, enabled = isUploadingImageEnabled)
                                .drawWithContent {
                                    drawContent()
                                    drawBorder(
                                        borderColor = borderColor,
                                        cornerRadiusValue = 12.dp().toPx()
                                    )
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Image(
                                modifier = Modifier.size(size = 24.dp),
                                painter = painterResource(resource = Res.drawable.ic_add_image),
                                contentDescription = stringResource(resource = Res.string.upload_dukan_image),
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/stephenWanjala/KomposeTable
            KotlinCode(
                code = """
                    //1 Correct way is to declare it with a @Composable() -> Unit key
                    val tableContent: @Composable() -> Unit = {
                        Text("Hello Table")
                    }
                    
                    //2 assign a Composable function literal to a variable
                    val tableContent = @Composable {
                        Column(modifier = modifier.fillMaxWidth()) {}
                    }
                    
                     if (outlinedTable) { 
                        OutlinedCard(
                            border = outlinedCardBorder,
                            shape = RoundedCornerShape(outlinedCardShape),
                            colors = CardDefaults.cardColors(containerColor = outlinedCardColor),
                            modifier = modifier,
                        ) {
                            tableContent()
                        }
                     }
                     else {
                        tableContent()
                     }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Column(
                        spacingUnits: Int,
                        horizontalAlignment: Alignment.Horizontal = Alignment.Start,
                        modifier: Modifier = Modifier,
                        content: @Composable ColumnScope.() -> Unit
                    ) {
                        Column(
                            horizontalAlignment = horizontalAlignment,
                            verticalArrangement = Pond.ruler.columnArrangement(spacingUnits),
                            modifier = modifier,
                            content = content
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Parameter LazyItemScope

                    @Composable
                    fun <K, V> LazyRowList(
                        items: Map<K, List<V>>,
                        modifier: Modifier = Modifier,
                        state: LazyListState = rememberLazyListState(),
                        contentPadding: PaddingValues = PaddingValues(0.dp),
                        reverseLayout: Boolean = false,
                        horizontalArrangement: Arrangement.Horizontal = if (!reverseLayout) Arrangement.Start else Arrangement.End,
                        verticalAlignment: Alignment.Vertical = Alignment.Top,
                        flingBehavior: FlingBehavior = ScrollableDefaults.flingBehavior(),
                        userScrollEnabled: Boolean = true,
                        itemContent: @Composable LazyItemScope.(V) -> Unit,
                    ) {
                        val itemsWithKeys = remember(items) {
                            items.flatMap { entry -> entry.value.map { entry.key to it } }
                        }

                        Column {
                            LazyRow(
                                modifier = modifier.fillMaxWidth(),
                                state = state,
                                contentPadding = contentPadding,
                                reverseLayout = reverseLayout,
                                horizontalArrangement = horizontalArrangement,
                                verticalAlignment = verticalAlignment,
                                flingBehavior = flingBehavior,
                                userScrollEnabled = userScrollEnabled,
                            ) {
                                items(
                                    items = itemsWithKeys,
                                ) { (_, value) ->
                                    itemContent(value)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalFoundationApi::class)

                    @Composable
                    fun LazyItemScope.CardPoster(
                        modifier: Modifier = Modifier,
                        data: Result,
                        loading: Boolean = false,
                        onClick: (Result) -> Unit
                    ) = CardPoster(
                        modifier.animateItemPlacement(
                            animationSpec = tween(durationMillis = 1000)
                        ), data, loading, onClick
                    )

                    @Composable
                    fun CardPoster(
                        modifier: Modifier = Modifier,
                        data: Result,
                        loading: Boolean = false,
                        onClick: (Result) -> Unit
                    ) {

                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Stable
                    sealed class ActionScope(private val ltr: Boolean) {
                    	@Stable
                    	object Left : ActionScope(true)
                    	@Stable
                    	object Right : ActionScope(false)

                    	@Composable
                    	fun Action(
                            icon: ImageVector,
                            color: Color = MaterialTheme.colorScheme.onSurface,
                            enabled: Boolean = true,
                            onClick: () -> Unit
                    	) {
                    		val padding = if (ltr) ThemeValue.Padding.HorizontalSpace else ThemeValue.Padding.ZeroSpace

                            ClickIcon(
                                icon = icon,
                                color = color,
                                enabled = enabled,
                                modifier = Modifier.Companion.padding(start = padding, end = ThemeValue.Padding.HorizontalSpace - padding),
                                onClick = onClick
                            )
                    	}

                    	@Composable
                    	fun ActionSuspend(
                            icon: ImageVector,
                            color: Color = MaterialTheme.colorScheme.onSurface,
                            enabled: Boolean = true,
                            onClick: suspend CoroutineScope.() -> Unit
                    	) {
                    		val padding = if (ltr) ThemeValue.Padding.HorizontalSpace else ThemeValue.Padding.ZeroSpace

                            LoadingIcon(
                                icon = icon,
                                color = color,
                                enabled = enabled,
                                modifier = Modifier.Companion.padding(start = padding, end = ThemeValue.Padding.HorizontalSpace - padding),
                                onClick = onClick
                            )
                    	}

                    	@Composable
                    	inline fun Actions(block: @Composable ActionScope.() -> Unit) = block()
                    }

                    @Composable
                    fun SplitLayout(
                        modifier: Modifier = Modifier,
                        aspectRatio: Float = 1f,
                        verticalAlignment: Alignment.Vertical = Alignment.CenterVertically,
                        gap: Dp = ThemeValue.Padding.HorizontalSpace,
                        left: @Composable BoxScope.() -> Unit = {},
                        right: @Composable BoxScope.() -> Unit = {}
                    ) {
                        Row(
                            modifier = modifier,
                            horizontalArrangement = Arrangement.spacedBy(gap),
                            verticalAlignment = verticalAlignment
                        ) {
                            Box(
                                modifier = Modifier.weight(aspectRatio),
                                contentAlignment = Alignment.CenterStart,
                                content = left
                            )
                            Box(
                                modifier = Modifier.weight(1f),
                                contentAlignment = Alignment.CenterEnd,
                                content = right
                            )
                        }
                    }

                    @Composable
                    fun SplitActionLayout(
                        modifier: Modifier = Modifier,
                        aspectRatio: Float = 1f,
                        verticalAlignment: Alignment.Vertical = Alignment.CenterVertically,
                        gap: Dp = ThemeValue.Padding.HorizontalSpace,
                        left: @Composable ActionScope.() -> Unit = {},
                        right: @Composable ActionScope.() -> Unit = {}
                    ) {
                        Row(
                            modifier = modifier,
                            horizontalArrangement = Arrangement.spacedBy(gap),
                            verticalAlignment = verticalAlignment
                        ) {
                            Row(
                                modifier = Modifier.weight(aspectRatio),
                                horizontalArrangement = Arrangement.Start,
                                verticalAlignment = Alignment.CenterVertically,
                                content = { ActionScope.Left.Actions(block = left) }
                            )
                            Row(
                                modifier = Modifier.weight(1f),
                                horizontalArrangement = Arrangement.End,
                                verticalAlignment = Alignment.CenterVertically,
                                content = { ActionScope.Right.Actions(block = right) }
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    enum class BorderType {
                        Horizontal, Vertical, None
                    }

                    @Composable
                    private fun LazyGridItemScope.ReorderableGridItem(
                        reorderState: ReorderableLazyGridState,
                        item: DisplayItem.SupabaseItem,
                        onDelete: (DisplayItem) -> Unit,
                        onClick: () -> Unit = {},
                        showBorders: Boolean = false,
                    ) {
                        ReorderableItem(
                            state = reorderState,
                            key = item.id,
                            modifier = Modifier.clickable(onClick = onClick)
                        ) { isDragging ->
                            val elevation = animateDpAsState(if (isDragging) 8.dp else 0.dp)

                            val hapticFeedback = LocalHapticFeedback.current

                            var borderType by remember { mutableStateOf(BorderType.None) }
                            val borderFraction = 1f / 36

                            Box(
                                modifier = Modifier
                                    .aspectRatio(3 / 4f)
                                    .shadow(elevation.value)
                                    .longPressDraggableHandle(onDragStarted = {
                                        hapticFeedback.performHapticFeedback(
                                            HapticFeedbackType.LongPress
                                        )
                                    })
                            ) {

                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/itxaliraza/Localizer
            KotlinCode(
                code = """
                    //Scroll Indicator

                    val scrollState = rememberLazyGridState()

                    @Composable
                    fun ColumnScope.LazyVerticalGridWithScrollIndicator(
                        scrollState: LazyGridState,
                        state: HomeScreenState,
                        viewModel: HomeScreenViewModel
                    ) {
                        val coroutineScope = rememberCoroutineScope()
                        val density = LocalDensity.current

                        var scrollbarY by remember { mutableStateOf(0f) }
                        var isDragging by remember { mutableStateOf(false) }

                        Row(modifier = Modifier.fillMaxSize().weight(1f)) {

                            // GRID CONTENT
                            LazyVerticalGrid(
                                columns = GridCells.Adaptive(145.dp),
                                state = scrollState,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .weight(1f)
                            ) {
                                items(state.filteredList, key = { it.langCode }) {
                                    LanguagesItems(
                                        model = it,
                                        isSelected = state.selectedLanguages.contains(it),
                                        selectAll = state.selectedLanguages.size == state.availableLanguages.size
                                    ) { model ->
                                        viewModel.updateSelectedLanguages(model)
                                    }
                                }
                            }

                            // SCROLLBAR LOGIC
                            val layoutInfo = scrollState.layoutInfo
                            val visibleItemsInfo = layoutInfo.visibleItemsInfo
                            val totalItemsCount = layoutInfo.totalItemsCount
                            val viewportHeight = layoutInfo.viewportSize.height.toFloat()

                            if (visibleItemsInfo.isNotEmpty() && totalItemsCount > 0) {
                                val firstItem = visibleItemsInfo.first()
                                val averageItemHeight =
                                    visibleItemsInfo.map { it.size.height }.average().toFloat().coerceAtLeast(1f)

                                val totalContentHeight = totalItemsCount * averageItemHeight
                                val scrollOffset =
                                    (firstItem.index * averageItemHeight - firstItem.offset.y).coerceAtLeast(0f)
                                val maxScrollOffset = (totalContentHeight - viewportHeight).coerceAtLeast(1f)

                                // Calculate scroll progress
                                val scrollProgress = (scrollOffset / maxScrollOffset).coerceIn(0f, 1f)

                                // Dynamic scrollbar height
                                val proportionVisible = viewportHeight / totalContentHeight
                                val scrollbarHeight = (viewportHeight * proportionVisible).coerceIn(50f, viewportHeight)

                                val trackHeight = viewportHeight - scrollbarHeight
                                scrollbarY = if (!isDragging) {
                                    (scrollProgress * trackHeight).coerceIn(0f, trackHeight)
                                } else scrollbarY

                                // SCROLL TRACK
                                Box(
                                    modifier = Modifier
                                        .padding(end = 4.dp)
                                        .width(8.dp)
                                        .fillMaxHeight()
                                        .background(Color.Gray.copy(alpha = 0.3f), RoundedCornerShape(4.dp))
                                ) {
                                    // SCROLL THUMB
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(with(density) { scrollbarHeight.toDp() })
                                            .offset { IntOffset(0, scrollbarY.toInt()) }
                                            .background(Color.DarkGray, RoundedCornerShape(4.dp))
                                            .pointerInput(Unit) {
                                                detectDragGestures(
                                                    onDragStart = { isDragging = true },
                                                    onDragEnd = { isDragging = false },
                                                    onDragCancel = { isDragging = false },
                                                    onDrag = { _, dragAmount ->
                                                        scrollbarY = (scrollbarY + dragAmount.y).coerceIn(0f, trackHeight)

                                                        val newScrollProgress = scrollbarY / trackHeight
                                                        val targetScrollOffset = newScrollProgress * maxScrollOffset
                                                        val targetIndex =
                                                            (targetScrollOffset / averageItemHeight).toInt()

                                                        coroutineScope.launch {
                                                            scrollState.scrollToItem(targetIndex)
                                                        }
                                                    }
                                                )
                                            }
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        @Composable
                        private fun Detail(
                            descriptionSection: @Composable () -> Unit,
                            propertiesSection: @Composable ColumnScope.() -> Unit,
                            modifier: Modifier = Modifier
                        ) {
                            Column(
                                modifier = modifier
                                    .fillMaxSize()
                                    .padding(horizontal = 16.dp)
                                    .verticalScroll(rememberScrollState()),
                                verticalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                propertiesSection()
                            }
                        }

                        @Composable
                        private fun PropertiesSection(
                            ibu: String,
                            style: String,
                            abv: String,
                        ): @Composable (ColumnScope.() -> Unit) = {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                            ) {
                                HeadingTextBlock(
                                    title = "Type",
                                    subtitle = style
                                )
                                HeadingTextBlock(
                                    title = "ABV",
                                    subtitle = abv
                                )
                                HeadingTextBlock(
                                    title = "IBU",
                                    subtitle = ibu
                                )
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun CheckboxItemSlot(
                        checked: Boolean,
                        onCheckedChanged: () -> Unit,
                        content: @Composable RowScope.() -> Unit,
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.clickable {
                                onCheckedChanged()
                            },
                        ) {
                            Checkbox(
                                checked = checked,
                                onCheckedChange = { onCheckedChanged() },
                            )
                            content()
                        }
                    }

                    @Composable
                    fun SlotEx() {
                        var checked1 by remember { mutableStateOf(false) }
                        var checked2 by remember { mutableStateOf(false) }

                        Column {
                            CheckboxItemSolt(
                                checked = checked1,
                                onCheckedChanged = { checked1 = !checked1 },
                            ) {
                                Text("1", modifier = Modifier.align(Alignment.CenterVertically))
                            }
                            CheckboxItemSlot(
                                checked = checked2,
                                onCheckedChanged = { checked2 = !checked2 },
                            ) {
                                Text("2", modifier = Modifier.align(Alignment.CenterVertically))
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/SakshamSharma2026/Vesto-Stock-market-App.git
            KotlinCode(
                code = """
                    //MarqueeRow 
                    
                    @Immutable
                    data class Company(
                        val name: String,
                        val ticker: String,
                        val iconPath: Int
                    )
                    
                    @Composable
                    fun MarqueeRow(
                        items: List<Company>,
                        modifier: Modifier = Modifier,
                        durationMillis: Int = 10000,
                        isReverse: Boolean = false
                    ) {
                        val infiniteTransition = rememberInfiniteTransition(label = "marquee")
                        val progress by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(durationMillis, easing = LinearEasing),
                                repeatMode = RepeatMode.Restart
                            ),
                            label = "progress"
                        )

                        Layout(
                            content = {
                                // Row 1
                                Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                    items.forEach { CompanyCard(it) }
                                }
                                // Row 2
                                Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                    items.forEach { CompanyCard(it) }
                                }
                            },
                            modifier = modifier
                                .fillMaxWidth()
                                .clipToBounds()
                        ) { measurables, constraints ->
                            val placeable = measurables.map {
                                it.measure(constraints.copy(minWidth = 0, maxWidth = Constraints.Infinity))
                            }

                            // Add space between the two rows
                            val spacing = 16.dp.roundToPx()
                            val rowWidth = placeable[0].width + spacing
                            val height = placeable.maxOf { it.height }

                            layout(constraints.maxWidth, height) {
                                // The calculated offset mapped to proper direction based on progress
                                val finalOffset = if (isReverse) {
                                    -rowWidth + (progress * rowWidth)
                                } else {
                                    -(progress * rowWidth)
                                }

                                // Using pure translation inside layerBlock effectively avoids recomposition 
                                // and relayout phases entirely, handling it super efficiently at drawing phase!
                                placeable[0].placeWithLayer(x = 0, y = 0) {
                                    translationX = finalOffset
                                }
                                placeable[1].placeWithLayer(x = rowWidth, y = 0) {
                                    translationX = finalOffset
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/kaaneneskpc/Deliverr.git
            KotlinCode(
                code = """
                    //LazyListScope GridView

                    fun LazyListScope.gridItems(
                        count: Int,
                        nColumns: Int,
                        horizontalArrangement: Arrangement.Horizontal = Arrangement.Start,
                        itemContent: @Composable BoxScope.(Int) -> Unit,
                    ) {
                        gridItems(
                            data = List(count) { it },
                            nColumns = nColumns,
                            horizontalArrangement = horizontalArrangement,
                            itemContent = itemContent,
                        )
                    }

                    fun <T> LazyListScope.gridItems(
                        data: List<T>,
                        nColumns: Int,
                        horizontalArrangement: Arrangement.Horizontal = Arrangement.Start,
                        key: ((item: T) -> Any)? = null,
                        itemContent: @Composable BoxScope.(T) -> Unit,
                    ) {
                        val rows = if (data.isEmpty()) 0 else 1 + (data.count() - 1) / nColumns
                        items(rows) { rowIndex ->
                            Row(horizontalArrangement = horizontalArrangement) {
                                for (columnIndex in 0 until nColumns) {
                                    val itemIndex = rowIndex * nColumns + columnIndex
                                    if (itemIndex < data.count()) {
                                        val item = data[itemIndex]
                                        androidx.compose.runtime.key(key?.invoke(item)) {
                                            Box(
                                                modifier = Modifier.weight(1f, fill = true),
                                                propagateMinConstraints = true
                                            ) {
                                                itemContent.invoke(this, item)
                                            }
                                        }
                                    } else {
                                        Spacer(Modifier.weight(1f, fill = true))
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Custom Column",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun CustomColumn(
                        modifier: Modifier = Modifier,
                        content: @Composable () -> Unit
                    ) {
                        Layout(
                            content = content,
                            modifier = modifier
                        ) { measurables, constraints ->
                            val placeables = measurables.map { it.measure(constraints) }
                            var yPosition = 0
                            layout(constraints.maxWidth, constraints.maxHeight) {
                                placeables.forEach { placeable ->
                                    placeable.placeRelative(x = 0, y = yPosition)
                                    yPosition += placeable.height
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://medium.com/@sivavishnu0705
            SpanText(
                text = "Can I still use lambdas with fun interface?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Yes! Because it's a \"Functional Interface,\" you can still do: val action = ToolbarAction { Text(\"Click Me\") }. This allows you to maintain clean syntax while gaining a distinct type identity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://medium.com/@sivavishnu0705
            SpanText(
                text = "Why use a class instead of just a function?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using a class/interface allows you to bundle data alongside the Composable logic, or to use sealed classes to restrict the slot to a very specific set of options (e.g., only \"Back\" or \"Close\" icons).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}